import { useEffect } from 'react';
import type { ModalDialogProps } from '@atlaskit/modal-dialog/types';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';

/**
 * Marks the persistent iframe host (see AiPlannerIframeProvider) so the modal's
 * focus lock can be told to ignore it while it is overlaid from outside the
 * dialog. See `focusLockAllowlist` below.
 */
export const AI_PLANNER_IFRAME_HOST_ATTRIBUTE = 'data-ai-planner-iframe-host';

export const AI_PLANNER_IFRAME_MODAL_TEST_ID = 'ai-planner-iframe-modal-dialog';

export const AI_PLANNER_IFRAME_MODAL_PRESENTATION_ATTRIBUTE =
	'data-ai-planner-iframe-modal-presentation';

export const AI_PLANNER_IFRAME_MODAL_RESIZE_TRANSITION_ATTRIBUTE =
	'data-ai-planner-iframe-modal-resize-transition';

export type PlannerModalPresentation = 'compact' | 'expanded';

const SIZING_STYLE_ELEMENT_ID = 'ai-planner-iframe-modal-sizing';

/**
 * Sizes ModalDialog's positioner from the presentation attribute owned by the
 * modal content. The positioner is an ancestor of the modal's own subtree, so
 * it can only be reached with a global rule. This is a workaround until a
 * maximized ADS modal is available to products.
 *
 * The compact presentation is tuned for Planner home. Both dimensions remain
 * bounded by the same viewport gutter as the expanded presentation.
 */
const modalSizingGlobalStyles = `
	.atlaskit-portal [data-testid="${AI_PLANNER_IFRAME_MODAL_TEST_ID}--positioner"] {
		--ai-planner-iframe-modal-width: calc(100vw - ${token('space.400')});
		--ai-planner-iframe-modal-height: calc(100vh - ${token('space.400')});
		inset: 0 !important;
		margin: auto !important;
		width: var(--ai-planner-iframe-modal-width);
		max-width: var(--ai-planner-iframe-modal-width);
		height: var(--ai-planner-iframe-modal-height);
		max-height: var(--ai-planner-iframe-modal-height);
	}

	.atlaskit-portal [data-testid="${AI_PLANNER_IFRAME_MODAL_TEST_ID}--positioner"][${AI_PLANNER_IFRAME_MODAL_PRESENTATION_ATTRIBUTE}][${AI_PLANNER_IFRAME_MODAL_RESIZE_TRANSITION_ATTRIBUTE}] {
		transition-property: transform, width, max-width, height, max-height;
		/* ADS sets the duration inline. Preserve its transform timing while making only resizing slower. */
		transition-duration:
			${token('motion.duration.medium')},
			${token('motion.duration.xlong')},
			${token('motion.duration.xlong')},
			${token('motion.duration.xlong')},
			${token('motion.duration.xlong')} !important;
	}

	.atlaskit-portal [data-testid="${AI_PLANNER_IFRAME_MODAL_TEST_ID}--positioner"][${AI_PLANNER_IFRAME_MODAL_PRESENTATION_ATTRIBUTE}="compact"] {
		--ai-planner-iframe-modal-width: min(908px, calc(100vw - ${token('space.400')}));
		--ai-planner-iframe-modal-height: min(740px, calc(100vh - ${token('space.400')}));
	}

	@media (prefers-reduced-motion: reduce) {
		.atlaskit-portal [data-testid="${AI_PLANNER_IFRAME_MODAL_TEST_ID}--positioner"][${AI_PLANNER_IFRAME_MODAL_PRESENTATION_ATTRIBUTE}][${AI_PLANNER_IFRAME_MODAL_RESIZE_TRANSITION_ATTRIBUTE}] {
			transition-property: none;
		}
	}
`;

/**
 * Props forwarded to the Planner iframe modal's underlying
 * `@atlaskit/modal-dialog` via `ModalTrigger`'s `modalProps`.
 *
 * A hook, not a constant, so that every trigger installs the sizing rule on
 * mount. The rule must reach the document before ModalDialog creates the
 * positioner: the modal is a lazily loaded EntryPoint, and Safari does not
 * re-resolve an already-styled positioner when the stylesheet arrives with that
 * chunk, leaving the dialog at ADS's default size. Pairing the rule with the
 * props is what stops a new trigger from silently missing it. The style element
 * is static and shared, so it is never removed.
 *
 * Gate-off, the iframe host is portaled to `document.body` and so sits outside
 * the modal, where its focus lock would keep keyboard focus out of the planner;
 * `focusLockAllowlist` excludes the host subtree. Gate-on the host is inside the
 * dialog, so the normal focus lock is correct and the allowlist is omitted.
 */
export const useAiPlannerIframeModalProps = (): ModalDialogProps => {
	useEffect(() => {
		if (typeof globalThis.document === 'undefined') {
			return;
		}
		if (globalThis.document.getElementById(SIZING_STYLE_ELEMENT_ID) != null) {
			return;
		}
		const style = globalThis.document.createElement('style');
		style.id = SIZING_STYLE_ELEMENT_ID;
		style.textContent = modalSizingGlobalStyles;
		globalThis.document.head.appendChild(style);
	}, []);

	return {
		width: '100%',
		height: '100%',
		testId: AI_PLANNER_IFRAME_MODAL_TEST_ID,
		...(fg('jira_planner_iframe_inside_modal')
			? {}
			: {
					focusLockAllowlist: (element: HTMLElement) =>
						element.closest(`[${AI_PLANNER_IFRAME_HOST_ATTRIBUTE}]`) == null,
				}),
	};
};
